import type { View, ViewContext } from '../router';
import { el, ensureStyle } from '../dom';
import { icon } from '../components/icons';
import {
  backBtn,
  emptyFocus,
  exitButton,
  ensureQuizStyle,
  fillProgress,
  focusSetup,
  focusTag,
  keyLegend,
  lengthField,
  markChoices,
  newBirdsField,
  missedList,
  quizHeader,
  quizKeys,
  retryTag,
  afterBar,
  compareButton,
  dontKnowButton,
  undoButton,
  segmented,
  tagSlot,
  type HearHandle,
  type KeyActions,
  type Miss,
} from '../components/quiz-chrome';
import { photoCredit, speciesPhoto } from '../components/species-photo';
import {
  buildPhotoSession,
  heroMirrored,
  photoPool,
  type PhotoMode,
  type PhotoQuestion,
} from '../../core/quiz/photo';
import { focusCodes, narrow, parseFocus } from '../../core/quiz/focus';
import { buildPhotoPairSession } from '../../core/quiz/pairs';
import { reaskPhoto } from '../../core/quiz/retry';
import { getSpecies } from '../../core/region';
import { imageUrl } from '../../core/media/image';
import { ratingFromCorrect } from '../../core/fsrs';
import { getSetting, setSetting } from '../../core/db';
import {
  newBirdShare,
  retryMisses,
  roundLength,
  setNewBirdShare,
  ROUND_LENGTHS,
  setRoundLength,
} from '../../core/quiz/settings';
import { XP_PER_CORRECT } from '../../core/progress';
import { clearUndo, gradeAnswer, undoAnswer } from '../../core/undo';
import { haptic } from '../haptics';
import { t } from '../../strings';

ensureQuizStyle();
ensureStyle(
  'quiz-photo-css',
  `
  /* Photo ID's chosen option takes its own skill colour; the rest of the setup
     screen is the shared one in quiz-chrome. */
  .photoq { --seg-on: var(--skill-photo); }
  .photoq .card { padding:26px 22px; text-align:center; }
  .photoq .card .front { font-family:var(--font-display);
    font-variation-settings:var(--fvs-display); font-size:27px; line-height:1.2; }
  /* The binomial under the prompt takes the panel's own soft ink — it used to
     ask for --ink-soft, a walnut grey that was never readable on a filled
     panel in either theme. */
  .photoq .card .sci { font-style:italic; color:var(--on-panel-soft); font-size:14px;
    margin-top:6px; }
  /* Answer grid: four photos, two up — the largest tile a phone fits without
     scrolling the options out of reach. */
  .photoq .grid { display:grid; grid-template-columns:1fr 1fr; gap:10px; }
  .photoq .grid button { position:relative; padding:0; line-height:0; overflow:hidden;
    border:2px solid var(--hairline); border-radius:var(--radius); background:var(--sunk); }
  .photoq .grid .spphoto { width:100%; aspect-ratio:1/1; }
  /* The same four states as a name list: your pick right, your pick wrong, the
     tile you should have picked, and the ones you never touched. */
  .photoq .grid button.correct { border-color:var(--moss); }
  .photoq .grid button.wrong { border-color:var(--danger); }
  .photoq .grid button.answer { border-color:var(--moss); }
  .photoq .grid button.dim { opacity:.45; }
  /* Named only once answered, so the grid itself never gives the answer away.
     An overlay strip rather than a caption below, so revealing the names can't
     reflow the grid under the user's finger. */
  .photoq .grid .nm { display:none; }
  .photoq .grid button.named .nm { display:flex; align-items:center; gap:6px;
    position:absolute; left:0; right:0; bottom:0;
    background:rgba(21,24,18,.72); color:var(--on-dark); font-family:var(--font-display);
    font-size:13px; line-height:1.25; text-align:left; padding:7px 8px 8px; }
  .photoq .grid button.correct .nm { background:var(--moss); }
  .photoq .grid button.wrong .nm { background:var(--danger); color:var(--on-danger); }
  .photoq .grid button.answer .nm { background:var(--moss-pale); color:var(--on-moss-pale); }
  /* The result mark rides the name bar rather than a corner disc on the photo —
     over a busy image at thumbnail size the disc was easy to miss. */
  .photoq .grid button.correct .nm::after, .photoq .grid button.wrong .nm::after,
  .photoq .grid button.answer .nm::after {
    content:''; flex:none; margin-left:auto; width:22px; height:22px;
    background:currentColor;
    -webkit-mask-repeat:no-repeat; mask-repeat:no-repeat;
    -webkit-mask-position:center; mask-position:center;
    -webkit-mask-size:contain; mask-size:contain; }
  .photoq .grid button.correct .nm::after, .photoq .grid button.answer .nm::after {
    -webkit-mask-image:var(--ico-check-bold); mask-image:var(--ico-check-bold); }
  .photoq .grid button.wrong .nm::after {
    -webkit-mask-image:var(--ico-cross-bold); mask-image:var(--ico-cross-bold); }
  .photoq .grid button.answer .nm::after { background:var(--moss); }
  /* Photo → Name has to fit a 4:3 hero, its credit, four choices AND the Next
     button on one phone screen, and at the shared quiz metrics it didn't — Next
     landed under the nav bar. The rows take a fixed height so the list is the
     same size whatever the binomials happen to be, and that height is set from
     what four of them plus everything else leaves over. */
  .photoq .choices { gap:8px; margin-top:0; }
  .photoq .choice { min-height:58px; padding:6px 52px 6px 16px; font-size:17px; }
  .photoq .choice .sci { font-size:12px; margin-top:1px; }
  .photoq .done .next { display:flex; width:100%; max-width:280px; margin:14px auto 0; }
  /* The hero and its credit are shared chrome now that the Field Test shows one
     too; see ensureQuizStyle, which also carries their two responsive passes. */
  @media (max-height:740px) {
    .photoq .choice { min-height:52px; }
  }
`,
);

const MODES: { value: PhotoMode; label: string; note: string }[] = [
  { value: 'mixed', label: t('quiz.round.mixed'), note: t('quiz.round.mixedNote') },
  { value: 'toName', label: t('quiz.photo.format.toName'), note: t('quiz.photo.format.toNameNote') },
  {
    value: 'toPhoto',
    label: t('quiz.photo.format.toPhoto'),
    note: t('quiz.photo.format.toPhotoNote'),
  },
];

export const quizPhotoView: View = async (root: HTMLElement, ctx: ViewContext) => {
  const [saved, savedLen, savedShare] = await Promise.all([
    getSetting<PhotoMode>('photo.mode'),
    roundLength('photo'),
    newBirdShare('photo'),
  ]);
  let mode: PhotoMode = MODES.some((m) => m.value === saved) ? (saved as PhotoMode) : 'mixed';
  // How many birds this round asks about; opens on whatever the last Photo ID
  // round was run with.
  let len = savedLen;
  // How much of the round is held for birds this learner has never met, as a
  // share of its length; see core/quiz/settings.ts.
  let share = savedShare;

  // The pair drill: the round is built from the birds this learner has actually
  // been calling each other, not from the schedule. See core/quiz/pairs.ts.
  const drill = ctx.params.drill === 'pairs';
  // A round started from the Library is confined to the birds that were on
  // screen when its button was pressed — see core/quiz/focus.ts. The drill has
  // a pool of its own and the two never combine. The focus lives in the hash
  // and nowhere else, so a reload keeps it and leaving the round drops it.
  const focus = drill ? null : parseFocus(ctx.params.focus);
  const pool = focus ? focusCodes(focus) : null;

  const wrap = el('div.quiz.photoq', {});
  root.append(wrap);

  let session: PhotoQuestion[] = [];
  // How many questions the round was dealt. Anything past it is a miss come
  // back for another go — see `retryMisses` — and is practice, not scored.
  let size = 0;
  let index = 0;
  let retryOn = false;
  // Whether the answer behind the undo button sent its question to the back.
  let requeued = false;
  let correctCount = 0;
  // The species this round got wrong, in the order they came up.
  let missed: Miss[] = [];
  /** Whether the answer the undo button stands behind was a right one. */
  let graded: boolean | null = null;
  let disposed = false;

  // One key listener for the whole visit, reading whatever the screen currently
  // offers. A listener added per render is a listener leaked per render.
  let keys: KeyActions | null = null;
  const releaseKeys = quizKeys(wrap, () => keys);

  // The speakers on the end-of-round Missed panel — the only sound this mode
  // makes, and the only thing in it that has to be silenced on the way out.
  let missSpeakers: HearHandle[] = [];

  function hush(): void {
    for (const s of missSpeakers) s.stop();
    missSpeakers = [];
  }

  // Only the first round of a visit inherits the daily card's hand-off.
  let firstRound = true;

  // `?go=1` — a round started from the Home card, which already knows what the
  // last one was set to. The setup screen is three taps between a learner and
  // the round they have already asked for; "Change options" at the end of the
  // round is the way back to one.
  if (ctx.params.go) void start();
  else renderSetup();

  /** What the round about to start is, in the length currently chosen. */
  function blurbText(): string {
    if (drill) {
      return t('quiz.photo.setup.drillBlurb', { n: len });
    }
    // An ordinary round gets no line at all. It used to promise "N species from
    // your review queue, drawn by the same schedule as <other mode>", which read
    // as a technical note about the scheduler on the one screen whose job is to
    // start a round — the title, the length and the options say everything a
    // learner needs here. The drill above keeps its line because a drill is NOT
    // an ordinary round, and nothing else on the screen says so.
    return '';
  }

  function renderSetup(): void {
    keys = null;
    hush();
    // Held onto so picking a length can rewrite the promise in place. Repainting
    // the whole screen would drop focus off the button that was just pressed.
    const text = blurbText();
    const blurb = text ? el('p.blurb.meta', {}, text) : null;
    const setup = focusSetup(
      focus,
      narrow(photoPool(), pool),
      len,
      t('quiz.photo.setup.lacking'),
    );
    len = setup.len;
    wrap.replaceChildren(
      el('div.bar', {}, backBtn(ctx)),
      el(
        'div.setup',
        {},
        el('h1', {}, t('common.mode.photo')),
        blurb,
        setup.note,
        setup.empty
          ? null
          : lengthField(
              len,
              (n) => {
                len = n;
                if (blurb) blurb.textContent = blurbText();
              },
              setup.lengths,
            ),
        // Not in a drill: that round is built from the birds this learner keeps
        // confusing, which are by definition birds they have already met.
        drill || setup.empty ? null : newBirdsField(share, (v) => (share = v)),
        el(
          'div.field',
          {},
          el('div.prompt.label', {}, t('quiz.round.format')),
          segmented(MODES, mode, (v) => (mode = v)),
        ),
        setup.empty
          ? emptyFocus(ctx)
          : el(
              'button.next.btn.btn-primary',
              { onclick: () => void start() },
              t('quiz.round.start'),
              icon('arrow-right', { size: 18 }),
            ),
      ),
    );
  }

  async function start(): Promise<void> {
    await setSetting('photo.mode', mode);
    // A length that came off a focus is a fact about how many sparrows there
    // are, not a preference — remembering it would hand the next ordinary round
    // a number the learner never chose.
    if (ROUND_LENGTHS.includes(len)) await setRoundLength('photo', len);
    await setNewBirdShare('photo', share);
    // The daily card grades its own answer before sending the learner here; the
    // bird it named in `?done=` sits out, and the round is one question shorter.
    // A second round started from the done screen is the full length again.
    session = drill
      ? await buildPhotoPairSession(mode, new Date(), Math.random, len)
      : await buildPhotoSession(
          mode,
          new Date(),
          Math.random,
          firstRound ? ctx.params.done : undefined,
          len,
          pool ?? undefined,
        );
    firstRound = false;
    retryOn = await retryMisses();
    size = session.length;
    index = 0;
    correctCount = 0;
    missed = [];
    graded = null;
    // Photos live in the media pack, which is an explicit download. Without it
    // every question would be a row of "photo not downloaded" placeholders, so
    // say that plainly instead of running an unanswerable session.
    if (session.length && !(await imageUrl(session[0].photo.file))) {
      if (!disposed) renderNoPack();
      return;
    }
    render();
  }

  function render(): void {
    if (disposed) return;
    keys = null;
    hush();
    wrap.replaceChildren();
    if (!session.length) return renderEmpty();
    if (index >= session.length) return renderDone();
    const q = session[index];
    if (q.direction === 'toPhoto') renderPickPhoto(q);
    else renderPickName(q);
  }

  /**
   * Persist the grade, award XP, keep the streak alive.
   *
   * `picked` is the species the learner chose — a name in one direction, a
   * photo in the other, the same fact either way — and it is what the mix-up
   * list and the pair drill are built out of.
   */
  async function grade(q: PhotoQuestion, correct: boolean, picked?: string): Promise<void> {
    const code = q.target.code;
    requeued = !correct && retryOn;
    if (requeued) session.push(reaskPhoto(q));
    if (index >= size) {
      // A comeback: nothing to record, and nothing for the undo to reach.
      graded = null;
      return;
    }
    if (correct) correctCount += 1;
    else missed.push({ code });
    graded = correct;
    await gradeAnswer({
      code,
      skill: 'photo',
      rating: ratingFromCorrect(correct),
      picked,
      xp: correct ? XP_PER_CORRECT : 0,
    });
  }

  /**
   * The inverse of `grade`, round tally included — the core half is in
   * core/undo.ts. Shared by both directions, which differ in how the answer is
   * shown and not at all in how it is scored.
   */
  function undoFor(answered: () => boolean): HTMLButtonElement {
    return undoButton(async () => {
      if (!answered() || graded === null || !(await undoAnswer())) return;
      if (graded) correctCount -= 1;
      else missed.pop();
      if (requeued) session.pop();
      requeued = false;
      graded = null;
      haptic('undo');
      // Same index, so the question is redrawn exactly as it was: the same
      // photograph, the same four choices, in the same order.
      render();
    });
  }

  /** The shared "Next"/"Finish" button; hidden until the question is answered. */
  function nextButton(): HTMLButtonElement {
    const btn = el(
      'button.next.btn.btn-primary',
      {},
      t('quiz.round.next'),
      icon('arrow-right', { size: 18 }),
    ) as HTMLButtonElement;
    btn.hidden = true;
    btn.addEventListener('click', () => {
      // Past this point the answer is no longer the one on screen.
      clearUndo();
      graded = null;
      index += 1;
      render();
    });
    return btn;
  }

  function reveal(btn: HTMLButtonElement, undo?: HTMLButtonElement): void {
    if (disposed) return;
    btn.hidden = false;
    if (undo) undo.hidden = graded === null;
    btn.replaceChildren(
      index + 1 >= session.length ? t('quiz.round.finish') : t('quiz.round.next'),
      icon('arrow-right', { size: 18 }),
    );
  }

  /** Photo → Name: the target's photo, four species names beneath it. */
  function renderPickName(q: PhotoQuestion): void {
    const head = quizHeader(ctx, index, session.length, t('quiz.round.nameTheBird'));
    if (focus) tagSlot(head).append(focusTag(focus, size));
    if (index >= size) tagSlot(head).append(retryTag());
    const flip = heroMirrored(q);
    const hero = speciesPhoto(q.photo, { alt: t('quiz.round.photoAlt'), mirror: flip });
    hero.element.classList.add('hero');

    const choices = el('div.choices', {});
    const buttons: HTMLButtonElement[] = [];
    let answered = false;
    const nextBtn = nextButton();
    const undoBtn = undoFor(() => answered);

    for (const code of q.choices) {
      const sp = getSpecies(code);
      const btn = el(
        'button.choice.btn.btn-quiet',
        {},
        el('div', {}, sp?.common ?? code),
        el('div.sci', {}, sp?.sci ?? ''),
      ) as HTMLButtonElement;
      buttons.push(btn);
      btn.addEventListener('click', () => void settle(code, btn));
      choices.append(btn);
    }

    // Opened on Photo, because a Photo ID mistake is a mistake about plumage —
    // the sound tabs are one swipe away for anyone who wants them.
    const compare = compareButton();

    const dontKnow = dontKnowButton(() => settle(null, null));

    const actions: KeyActions = {
      choices: buttons,
      next: nextBtn,
      undo: undoBtn,
      skip: dontKnow,
    };
    keys = actions;

    /** Everything answering does, whether a bird was named or not. */
    async function settle(code: string | null, btn: HTMLButtonElement | null): Promise<void> {
      if (answered) return;
      answered = true;
      const correct = code === q.target.code;
      haptic(correct ? 'right' : 'wrong');
      markChoices(buttons, q.choices, btn ? buttons.indexOf(btn) : -1, q.target.code);
      // The bar has to count the question just answered, or the last question of
      // a round sits at 90% while its result is on screen.
      fillProgress(head, index, session.length);
      dontKnow.hidden = true;
      // No second bird to hold this one against, and nothing for the mix-up
      // record: not knowing is not confusing.
      if (code) compare.offer(q.target.code, code, 'photo');
      await grade(q, correct, code ?? undefined);
      reveal(nextBtn, undoBtn);
    }

    wrap.append(
      head,
      hero.element,
      // The credit names the photographer, never the bird, so it is safe to show
      // before the answer.
      el('div.attrib.note', {}, photoCredit(q.photo, flip)),
      choices,
      dontKnow,
      keyLegend(actions),
      afterBar(undoBtn, nextBtn, compare.element),
    );
  }

  /** Name → Photo: the species named, four photos to pick between. */
  function renderPickPhoto(q: PhotoQuestion): void {
    const head = quizHeader(ctx, index, session.length, t('quiz.photo.question.pickPhoto'));
    if (focus) tagSlot(head).append(focusTag(focus, size));
    if (index >= size) tagSlot(head).append(retryTag());
    const grid = el('div.grid', {});
    const buttons: HTMLButtonElement[] = [];
    let answered = false;
    const nextBtn = nextButton();
    const undoBtn = undoFor(() => answered);

    q.choices.forEach((code, i) => {
      const sp = getSpecies(code);
      const btn = el(
        'button',
        { 'aria-label': t('quiz.photo.question.tileLabel', { n: i + 1 }) },
        speciesPhoto(q.photos[i], {
          alt: t('quiz.photo.question.tileAlt', { n: i + 1 }),
          mirror: q.mirrored[i],
        }).element,
        el('div.nm', {}, sp?.common ?? code),
      ) as HTMLButtonElement;
      buttons.push(btn);
      btn.addEventListener('click', () => void settle(code, btn));
      grid.append(btn);
    });

    const compare = compareButton();

    const dontKnow = dontKnowButton(() => settle(null, null));

    const actions: KeyActions = {
      choices: buttons,
      next: nextBtn,
      undo: undoBtn,
      skip: dontKnow,
    };
    keys = actions;

    /** Everything answering does, whether a photo was picked or not. */
    async function settle(code: string | null, btn: HTMLButtonElement | null): Promise<void> {
      if (answered) return;
      answered = true;
      const correct = code === q.target.code;
      haptic(correct ? 'right' : 'wrong');
      const picked = btn ? buttons.indexOf(btn) : -1;
      dontKnow.hidden = true;
      fillProgress(head, index, session.length);
      buttons.forEach((b, i) => {
        b.disabled = true;
        // Naming every tile turns the three wrong photos into the lesson: this
        // is the bird you nearly picked.
        b.classList.add('named');
        if (i === picked) b.classList.add(correct ? 'correct' : 'wrong');
        else if (q.choices[i] === q.target.code) b.classList.add('answer');
        else b.classList.add('dim');
      });
      if (code) compare.offer(q.target.code, code, 'photo');
      await grade(q, correct, code ?? undefined);
      reveal(nextBtn, undoBtn);
    }

    wrap.append(
      head,
      // sk-photo: the prompt card is the Photo ID skill, so it takes the same
      // teal as that skill's study card, ring and daily round.
      el(
        'div.card.panel.sk-photo',
        {},
        el('div.front', {}, q.target.common),
        el('div.sci', {}, q.target.sci),
      ),
      grid,
      dontKnow,
      keyLegend(actions),
      afterBar(undoBtn, nextBtn, compare.element),
    );
  }

  function renderEmpty(): void {
    wrap.append(
      el('div.bar', {}, backBtn(ctx)),
      el(
        'div.done',
        {},
        el('div.big', {}, t('quiz.round.nothingToStudy')),
        el(
          'div.stat',
          {},
          drill ? t('quiz.photo.empty.drill') : t('quiz.photo.empty.noPhotos'),
        ),
      ),
    );
  }

  function renderNoPack(): void {
    wrap.replaceChildren(
      el('div.bar', {}, backBtn(ctx)),
      el(
        'div.done',
        {},
        el('div.big', {}, t('quiz.round.noPackTitle')),
        el('div.stat', {}, t('quiz.photo.noPack.body')),
        el(
          'button.next.btn.btn-primary',
          { onclick: () => ctx.navigate('/you') },
          t('quiz.round.goToDownloads'),
        ),
      ),
    );
  }

  function renderDone(): void {
    const pct = Math.round((correctCount / size) * 100);
    // Another round in the same format is what a finished round almost always
    // wants next; going back through the setup screen to re-pick the format you
    // just used is the rarer case, so it keeps the quiet button.
    const again = el(
      'button.next.btn.btn-primary',
      { onclick: () => void start() },
      t('quiz.round.again'),
      icon('arrow-right', { size: 18 }),
    ) as HTMLButtonElement;
    keys = { next: again };
    // The one screen in this mode that makes a sound: a bird you could not name
    // is still one worth hearing, so each miss plays its song where it stands.
    const panel = missedList(missed, ctx);
    missSpeakers = panel?.speakers ?? [];
    wrap.append(
      el('div.bar', {}, backBtn(ctx)),
      el(
        'div.done',
        {},
        el(
          'div.big',
          {},
          t('quiz.round.correct', { scored: correctCount, total: size }),
        ),
        el(
          'div.stat',
          {},
          t('quiz.round.stat', { pct, xp: correctCount * XP_PER_CORRECT }),
        ),
        panel?.element ?? null,
        again,
        el(
          'button.next.btn.btn-quiet',
          { onclick: () => renderSetup() },
          t('quiz.round.changeOptions'),
        ),
        exitButton(ctx, Boolean(focus)),
      ),
    );
  }

  return () => {
    disposed = true;
    releaseKeys();
    hush();
    // Leaving a round mid-answer takes the undo with it: the question it
    // belonged to is off the screen, so nothing may reach back to it.
    clearUndo();
  };
};
